import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  lazy,
  Suspense,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { IoMdClose } from 'react-icons/io';
import { openThankYouAfterSubmitPayload } from '../utils/buyThankYouNavigation';
import '../components/ThankYouModal/ThankYouLeadModal.css';

const ThankForContactingApartments = lazy(() =>
  import('../components/ThankForContactingApartments/ThankForContactingApartments'),
);
const RentsThankForContactingApartments = lazy(() =>
  import('../components/RENTS/RentsThankForContactingApartments/RentsThankForContactingApartments'),
);

const ThankYouModalContext = createContext(null);

export function ThankYouModalProvider({ children }) {
  const [modal, setModal] = useState(null);
  const location = useLocation();

  const openThankYouModal = useCallback((args) => {
    const payload = openThankYouAfterSubmitPayload(args);
    if (!payload) return;
    setModal(payload);
  }, []);

  const closeThankYouModal = useCallback(() => {
    setModal(null);
  }, []);

  // Close overlay when route changes (e.g. Proser recommendation card Link)
  useEffect(() => {
    setModal(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!modal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeThankYouModal();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('modal-open');
    };
  }, [modal, closeThankYouModal]);

  return (
    <ThankYouModalContext.Provider value={{ openThankYouModal, closeThankYouModal }}>
      {children}
      {modal &&
        createPortal(
          <div
            className="thank-you-modal-root fixed inset-0 z-[10050] flex items-center justify-center p-2 sm:p-3"
            role="dialog"
            aria-modal="true"
            aria-label="Thank you"
          >
            <button
              type="button"
              className="thank-you-modal-backdrop absolute inset-0 z-0 min-h-[100dvh] w-full cursor-default border-0 bg-black/50 p-0 backdrop-blur-[2px]"
              onClick={closeThankYouModal}
              aria-label="Close thank you dialog"
            />
            <div
              className="thank-you-modal-panel-host"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="thank-you-modal-close-fab"
                onClick={closeThankYouModal}
                aria-label="Close"
              >
                <IoMdClose className="text-xl" aria-hidden />
              </button>
              <div className="thank-you-modal-scroll">
                <Suspense
                  fallback={
                    <div className="thank-you-modal-suspense">Loading…</div>
                  }
                >
                  {modal.isRentListing ? (
                    <RentsThankForContactingApartments
                      routeState={modal.routeState}
                      variant="modal"
                    />
                  ) : (
                    <ThankForContactingApartments
                      property_type={modal.listingType}
                      routeState={modal.routeState}
                      variant="modal"
                    />
                  )}
                </Suspense>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ThankYouModalContext.Provider>
  );
}

export function useThankYouModal() {
  const ctx = useContext(ThankYouModalContext);
  if (!ctx) {
    throw new Error('useThankYouModal must be used within ThankYouModalProvider');
  }
  return ctx;
}
